import type { ValidationRule } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

/**
 * SomeRule is shortcut type for validation rules.
 */
type SomeRule = string | ValidationRule<boolean> | undefined | any;

/**
 * useRules hook
 * This hook is used to apply predefined validation rules to the field.
 * This hook apply localizations to the rules where it is needed.
 *
 * @public
 * @param rules - validation rules
 */
export const useRules = (rules: Record<string, SomeRule> = {}) => {
  const { t } = useTranslation(['common']);
  const { minLength, maxLength, required, ...restRules } = rules;

  const normalizationRule = (rule: SomeRule, locKey: string) =>
    typeof rule === 'object'
      ? {
          message: locKey,
          ...rule,
        }
      : {
          value: rule,
          message: locKey,
        };

  /**
   * applyLocalization apply localizations when provided rule is set to true.
   * When rule is set to string, it is considered as a localization key.
   */
  const applyLocalization = (rule: SomeRule, locKey: string): SomeRule => {
    if (rule === true) {
      return t(locKey);
    }

    if (typeof rule === 'string') {
      return t(rule);
    }

    return rule;
  };

  const applyLocalizationMinLength = (
    rule: SomeRule,
    locKey: string = 'common:formErrors.minNumberOfSigns',
  ): SomeRule => {
    const normalizedRule = normalizationRule(rule, locKey);

    return {
      ...normalizedRule,
      message: t(locKey, { min: normalizedRule.value }),
    };
  };

  const applyLocalizationMaxLength = (
    rule: SomeRule,
    locKey: string = 'common:formErrors.maxNumberOfSigns',
  ): SomeRule => {
    const normalizedRule = normalizationRule(rule, locKey);

    return {
      ...normalizedRule,
      message: t(locKey, { max: normalizedRule.value }),
    };
  };

  return {
    minLength: applyLocalizationMinLength(minLength),
    maxLength: applyLocalizationMaxLength(maxLength),
    required: applyLocalization(required, 'common:formErrors.required'),
    ...restRules,
  };
};
